import {take} from "rxjs/operators";
import {FlowDescriptionRule} from "@app/netscope/model/data-models";
import {FlowsRepository} from "@app/netscope/model/flows-repository";
import {AppsGroupsResolver} from "@app/netscope/model/apps-groups-resolver";
import {NetscopeApiService, Success} from "@app/services/netscope-api.service";

/**
 * How the links of the apps groups view take the flows descriptions into account:
 *  - 'ignore': a single link between two nodes, as if there were no description,
 *  - 'status': one link per status of the flows it carries (authorized, unauthorized, undescribed),
 *  - 'descriptions': one link per description matching some of its flows, named after it, and one for the
 *    flows no description matches. A flow matching several descriptions is carried by each of their links.
 */
export type AppsGroupsLinksMode = "ignore" | "status" | "descriptions";

/**
 * Part of the flows between two nodes a link carries, and how that link is drawn.
 */
export class LinkCategory {
  // Written in the id of the link: holds no '__', which separates the two ends of a link
  key: string;
  // Text written along the link, if any
  label?: string;
  // What the category stands for, shown on hover
  title: string;
  color: string;
  dashed: boolean = false;
  // SQL clause matching the flows of the category, to list them in the side panel
  sqlClause: string;
}

const COLORS = {
  authorized: "#2e8540",
  unauthorized: "#c92100",
  undescribed: "#8c8c8c",
};

// An expression over a flow evaluating to true or false, never to NULL: a NULL address does not match a network
const truthy = (clause: string): string => `coalesce((${clause}), false)`;

export class FlowsDescriptionsClassification {
  // Categories of the flows of every pair of resources, keyed by 'source uuid__destination uuid'
  private categoriesByPair: Map<string, LinkCategory[]> = new Map();

  // Category of the flows of a pair the query did not return, which no description can therefore be told about
  private fallbackCategory: LinkCategory;

  /**
   * Categories of the flows going from a resource to another one, as the flows of the view hold them. Every
   * pair holds at least one category, so that no flow gets lost when the links are split.
   */
  categoriesOf = (sourceUuid: string, targetUuid: string): LinkCategory[] => {
    return this.categoriesByPair.get(`${sourceUuid}__${targetUuid}`) ?? [this.fallbackCategory];
  }

  /**
   * Reads the flows descriptions, and tells which of them match the flows of every pair of resources. The apps
   * groups the descriptions point to are resolved first, as the flows descriptions page does it.
   */
  static async classify(mode: AppsGroupsLinksMode, flowsRepository: FlowsRepository,
                        netscopeService: NetscopeApiService): Promise<FlowsDescriptionsClassification> {
    if (mode == "ignore" || flowsRepository == undefined) {
      return undefined;
    }

    let response = await netscopeService.getFlowsDescriptions().pipe(take(1)).toPromise();
    if (!(response instanceof Success)) {
      console.warn("[apps-groups] the flows descriptions could not be loaded, the links ignore them");
      return undefined;
    }

    let descriptions = (response.element.flowsDescriptions ?? [])
      .map((description) => FlowDescriptionRule.from(description));

    let resolver = new AppsGroupsResolver(netscopeService);
    await resolver.loadFiles(descriptions
      .flatMap((description) => description.networkRules)
      .flatMap((networkRule) => networkRule.appsGroupsReferences())
      .map((reference) => reference.file));

    let descriptionsClauses = descriptions.map((description) => description.toSqlClause(resolver));

    // A flow is described as soon as a description matches it, and unauthorized as soon as an unauthorized one
    // does, whatever the authorized ones say: the flows descriptions page counts them the same way
    let describedClause = orOf(descriptionsClauses.map(truthy));
    let unauthorizedClause = orOf(descriptionsClauses
      .filter((clause, index) => !descriptions[index].authorized)
      .map(truthy));

    let undescribed = new LinkCategory();
    undescribed.key = "undescribed";
    undescribed.title = "Flows no description matches";
    undescribed.color = COLORS.undescribed;
    undescribed.dashed = true;
    undescribed.sqlClause = `not (${describedClause})`;

    let categories: LinkCategory[] = [];

    if (mode == "status") {
      let authorized = new LinkCategory();
      authorized.key = "authorized";
      authorized.title = "Flows matching authorized descriptions only";
      authorized.color = COLORS.authorized;
      authorized.sqlClause = `(${describedClause}) and not (${unauthorizedClause})`;

      let unauthorized = new LinkCategory();
      unauthorized.key = "unauthorized";
      unauthorized.title = "Flows matching an unauthorized description";
      unauthorized.color = COLORS.unauthorized;
      unauthorized.sqlClause = unauthorizedClause;

      categories = [authorized, unauthorized, undescribed];
    } else {
      categories = descriptions.map((description, index) => {
        let category = new LinkCategory();
        category.key = `description:${index}`;
        category.label = description.name;
        category.title = `Flows matching the description '${description.name}' (${description.authorized ? "authorized" : "unauthorized"})`;
        category.color = description.authorized ? COLORS.authorized : COLORS.unauthorized;
        category.sqlClause = truthy(descriptionsClauses[index]);
        return category;
      });
      categories.push(undescribed);
    }

    let rows = await flowsRepository.aggregateFlowsPairs(categories.map((category, index) => ({
      alias: `category_${index}`,
      expression: `bool_or(${truthy(category.sqlClause)})`,
    })));

    let classification = new FlowsDescriptionsClassification();
    classification.fallbackCategory = undescribed;

    for (let row of rows) {
      let pairCategories = categories.filter((category, index) => row[`category_${index}`]);
      classification.categoriesByPair.set(`${row.src_resource_uuid}__${row.dst_resource_uuid}`, pairCategories);
    }

    return classification;
  }
}

function orOf(clauses: string[]): string {
  return clauses.length == 0 ? "false" : clauses.map((clause) => `(${clause})`).join(" or ");
}
